import dotProp from 'dot-prop';
import get from 'lodash/get';
import remove from 'lodash/remove';
import trim from 'lodash/trim';
import includes from 'lodash/includes';
import {Types} from './actions';
import ReducerBuilder from 'classes/ReducerBuilder';
import * as ResourceApi from 'api/resources';
import initialState, {makeDefaultAllResources, makeDefaultResourcePages} from './initialState';
import {emptyPageFactory} from "./factories";
import {filterOptionsFactory, sortOptionsFactory} from 'viewOptions/factories';
import {Types as BreakActionTypes} from 'pages/EditBreak/actions';
import {breakImagesPageName} from 'pages/EditBreak/utils';
import {Types as SectionActionTypes} from 'utils/sectionManagement/actions';
import {clone, getBreakResource, isNoU, setBreakResource} from "../utils/DBUtils";
import setAfterSaveConfig from './setAfterSaveConfig';
import reorderSaveConfig from './reorderSaveConfig';
import {Types as UserActionTypes} from "../pages/Login/actions";

// Resource that are allowed in the root of resources state object
const topLevelResources = [
    ResourceApi.Types.Challenge,
    ResourceApi.Types.Break,
    ResourceApi.Types.Image,
    ResourceApi.Types.Prize,
    ResourceApi.Types.Palette,
    ResourceApi.Types.RetailerItem,
    ResourceApi.Types.StoreCatalog,
    ResourceApi.Types.StoreEscrow,
    ResourceApi.Types.BreakTag,
];

// resource fields regexps that we want to avoid updating in this reducer on
// SENT_SAVE_RESOURCE_FIELD, so that we can instead defer to the value returned from the
// server.
// handy for something like Prize#drawInfo that employs a special shorthand syntax.
const ignoreOnSentSaveField = Object.freeze({
    [ResourceApi.Types.Prize]: [
        /^drawInfo\..*$/
    ]
});

const shouldIgnoreOnSentSaveField = (rootResourceId, field) => {
    const type = rootResourceId.type;
    const ignoredPatterns = get(ignoreOnSentSaveField, type, []);
    return Boolean(ignoredPatterns.find(pattern => pattern.test(field)));
};

const getArrayWithFieldPath = (resource, fieldPath) => {
    // Slice out an index param if present, so we can push to array
    // ex. some path items are formatted 'field.0', so it becomes just 'field'
    //     but leaves a path item 'field' as 'field'
    let lastField = fieldPath[fieldPath.length - 1];
    let parts = lastField.split(".");
    let aryFieldPath = fieldPath.slice(0, -1).concat(parts[0]);

    // Get the array, or create one if none exists at that path
    let path = aryFieldPath.join('.');
    let array = dotProp.get(resource, path);
    if (!array) {
        dotProp.set(resource, path, []);
        array = dotProp.get(resource, path);
    }

    // Push new resource to the array
    return array;
};

/**
 * Adds the provided resource to an array found at the given fieldPath.
 * If the provided resource is a top level resource it is added to the root of resources instead of using the path.
 * @param resources
 * @param resource
 * @param rootResource
 * @param fieldPath
 */
const addResourceToResources = (resources, resource, rootResource, fieldPath) => {

    if (resource.resourceId.type === ResourceApi.Types.Challenge) {
        // Special case, possible should be moved else where
        resources[resource.breaks.MASTER.id] = resource.breaks.MASTER;
    } else if (topLevelResources.indexOf(resource.resourceId.type) > -1) {
        resources[resource.resourceId.id] = resource;
    } else if (rootResource) {
        let root = resources[rootResource.resourceId.id]; // legacy code edits this version, not rootResource

        // beaker assumed resources were always added to arrays.
        // WAS 
        // let aryField = getArrayWithFieldPath(root, fieldPath);
        // aryField.push(resource);

        let fieldPathParent = JSON.parse(JSON.stringify(fieldPath)).join('.').split(".");
        
        const destiantionFieldName = fieldPathParent.pop()
        const parent = dotProp.get(root, fieldPathParent.join('.'))       // parent is a refernce in the source object


        const destination = dotProp.get(root, fieldPath.join('.'))
        // if we're adding a resource to a resource destination could be undefined (it's being created now)
        console.log(`destination`, destination, Array.isArray(destination))

        if (Array.isArray(destination)) {
            console.log("Adding ARRAY ITEM")
            destination.push(resource)
        } else {
            console.log("Adding PROPERTY")
            dotProp.set(root, fieldPath.join("."), resource);
            // parent["beakermod"] = new Date()    // hack. 'touch' parent to get it to re-render
            console.log(fieldPath.join("."), dotProp.get(root, fieldPath.join(".")))
        }

    }
};

const removeResourceFromResources = (resources, resource, rootResource, fieldPath) => {
    if (topLevelResources.indexOf(resource.resourceId.type) > -1) {
        delete resources[resource.resourceId.id];
    } else {
        let root = resources[rootResource.resourceId.id]; // legacy code edits this version, not rootResource

        // for (let i = 0; i < fieldPath.length; i++) {
        //     const node = fieldPath.slice(0, i)
        //     const element = dotProp.get(root, node.join('.'))
        //     if (element && element.resourceId && element.resourceId.id) {
        //         console.log(node.join('.'), resources[element.resourceId.id])
        //     }
            
        // }

        console.log(`removed`, fieldPath)
        // fieldPath might look like: ["steps.0", "poll", "questions.1"]
        // now it loos like ["steps","0", "poll", "questions", "1"]
        let fieldPathParent = JSON.parse(JSON.stringify(fieldPath)).join('.').split(".");
        
        const fieldNameToRemove = fieldPathParent.pop()

        const parent = dotProp.get(root, fieldPathParent.join('.'))       // parent is a refernce in the source object
        console.log(`parent`, parent, Array.isArray(parent))
        
        if (Array.isArray(parent)) {
            console.log("Removing ARRAY ITEM")
            let toDeleteIndex = parent.findIndex((item) => item.resourceId.id === resource.resourceId.id);
            parent.splice(toDeleteIndex, 1);
            let count = 1;
            for (let item of parent) {
                item.displayOrder = count;
                count++;
            }
        } else {
            console.log("Removing PROPERTY")
            // for v1 we're removing a leaf node
            // remove the last item in our array

            console.log(`fieldPath`, fieldPath.join('.'))

            // let foo = dotProp.get(root, fieldPath.join('.'))
            // console.log(`foo`, foo)
            delete parent[fieldNameToRemove]
            // parent["beakermod"] = new Date()
            // console.log(`foo`, foo)
            console.log(`root`, root)
            console.log(fieldPath.join("."), dotProp.get(root, fieldPath.join(".")))

        }

    }
};

const removeStepsFromResource = (resources, resource, stepIds) => {
    const {steps} = resources[resource.resourceId.id];
    stepIds.forEach(stepId => remove(steps, s => s.id === stepId));
    let count = 1;
    for (let step of steps) {
        step.displayOrder = count;
        count++;
    }
};

const updateModifiedBreak = (state, modifiedBreak) => {
    if (!isNoU(modifiedBreak)) {
      let brk = state.resources[modifiedBreak.id];

      if (!isNoU(modifiedBreak.defaultParentId)) {
          let master = state.resources[modifiedBreak.defaultParentId];
          master.possibleCoinsAwarded = modifiedBreak.possibleCoinsAwarded;
      }

      brk.possibleCoinsAwarded = modifiedBreak.possibleCoinsAwarded;
    }
};

const reducer = ReducerBuilder.make(initialState)
    .forType(Types.CREATE_RESOURCE).reduce((state, action) => state)
    .forType(Types.CREATED_RESOURCE)
    .reduce((state, {resource, rootResource, fieldPath, modifiedBreak}) => {
        if (Array.isArray(resource)) {
            for (let r of resource) {
                addResourceToResources(state.resources, r, rootResource, fieldPath);
            }
        } else {
          addResourceToResources(state.resources, resource, rootResource, fieldPath);
        }
        console.log("before update")
        updateModifiedBreak(state, modifiedBreak);
    })
    .forType(Types.SYNCHRONIZE_RESOURCE).reduce((state, action) => state)
    .forType(Types.SYNCHRONIZED_RESOURCE).reduce((state, action) => state)
    .forType(Types.DELETE_RESOURCE).reduce((state, action) => state)
    .forType(Types.DELETED_RESOURCE)
    .reduce((state, action) => {
        const {resource, rootResource, fieldPath} = action;

        const stepIds = get(action, 'data.stepIds', null);
        if (stepIds) {
            removeStepsFromResource(state.resources, resource, stepIds);
        } else {
            removeResourceFromResources(state.resources, resource, rootResource, fieldPath);
        }

    //   updateModifiedBreak(state, action.modifiedBreak);
    })
    .forType(Types.GET_RESOURCE).reduce((state, action) => state)
    .forType(Types.RECEIVED_RESOURCE)
    .reduce((state, action) => {
        state.resources[action.resource.id] = action.resource;
    })
    .forType(Types.GET_ALL_RESOURCES).reduce((state, action) => state)
    .forType(Types.RECEIVED_ALL_RESOURCES)
    .reduce((state, action) => {
        for (let resource of action.resources) {
            state.resources[resource.id] = resource;
        }
    })
    .forType(BreakActionTypes.RECEIVED_ALL_BREAK_IMAGES)
    .reduce((state, action) => {
      if (!state.allResources[breakImagesPageName]) {
        state.allResources[breakImagesPageName] = {};
      }

      state.allResources[breakImagesPageName].content = action.images.map(({id}) => id);
    })
    .forType(Types.GET_RESOURCE_PAGE).reduce((state, {pageId, resourceType, resourcePage, sortOptions, filterOptions}) => {
        let pageName = pageId || resourceType;
        if (isNoU(state.resourcePages[pageName])) {
            state.resourcePages[pageName] = makeDefaultResourcePages();
        }

        state.resourcePages[pageName].fetching = true;
    })
    .forType(Types.RECEIVED_RESOURCE_PAGE)
    .reduce((state, {pageId, resourceType, resourcePage, sortOptions, filterOptions}) => {
        let oldPage = state.resourcePages[resourceType];
        let pageName = pageId || resourceType;
        state.resourcePages[pageName] = {
            page: isNoU(resourcePage.page) ? oldPage.page : resourcePage.page,
            count: isNoU(resourcePage.count) ? oldPage.count : resourcePage.count,
            total: isNoU(resourcePage.total) ? oldPage.total : resourcePage.total,
            sortOptions: sortOptions ? clone(sortOptions) : sortOptionsFactory(),
            filterOptions: filterOptions ? clone(filterOptions) : filterOptionsFactory(),
            content: resourcePage.object.map((item) => item.id),
            fetching: false
        };

        let resources = resourcePage.object;
        for (let resource of resources) {
            state.resources[resource.id] = resource;
        }
        return state;
    })
    .forType(Types.CLEAR_RESOURCE_PAGE)
    .reduce((state, action) => {
        state.resourcePages[action.resourceType] = emptyPageFactory();
    })
    .forType(Types.SENT_SAVE_RESOURCE_FIELD)
    .reduce((state, {id, field, value, rootResourceId, masterResourceId, resourcePath, fieldPath, errors}) => {
        if (shouldIgnoreOnSentSaveField(rootResourceId, fieldPath)) {
            return;
        }

        let rootResource = state.resources[rootResourceId.id];
        let masterResource = rootResource;

        if (!isNoU(rootResource)
          && !isNoU(rootResource.platform)
          && rootResource.resourceId.type === ResourceApi.Types.Break
          && rootResource.platform !== "MASTER") {
            masterResource = state.resources[rootResource.defaultParentId];
        }

        let modifiedResource = rootResource;
        let masterModifiedResource = masterResource;

        // Get explicit reference to modified resource for type field check
        if (resourcePath.length > 0) {
            // modifiedResource = dotProp.get(rootResource, resourcePath.join('.'), {})
          modifiedResource = getBreakResource(masterResource, rootResource, resourcePath) || {};
          masterModifiedResource = getBreakResource(masterResource, masterResource, resourcePath) || {};
        }

        // if the type field combination is in reorderSaveConfig
        if (!isNoU(modifiedResource)
            && !isNoU(reorderSaveConfig[modifiedResource.resourceId.type])
            && !isNoU(reorderSaveConfig[modifiedResource.resourceId.type][field])) {

            let fieldToReorder = reorderSaveConfig[modifiedResource.resourceId.type][field];
            // console.log(`reordering ${field} on ${modifiedResource.resourceId.type}`);
            // let fieldValue = dotProp.get(modifiedResource, fieldToReorder, []);
            let fieldValue = getBreakResource(masterModifiedResource, modifiedResource, [fieldToReorder]) || [];

            // Get Master field value to be referenced if the non-Master's display orders have yet to be set
            let masterFieldValue = null;
            if (rootResourceId.type === ResourceApi.Types.Break && rootResource.platform !== "MASTER") {
                let masterId = rootResource.defaultParentId;
                let masterRoot = state.resources[masterId];
                if (!isNoU(masterRoot)) {
                    masterFieldValue = dotProp.get(masterRoot, fieldToReorder, []);
                }
            }
            if (!Array.isArray(fieldValue)) {
                console.error(`field set as reorder but is not an array. ${field} on`, modifiedResource);
            }

            // loop through each new display order
            let newOrderList = value.map((oldOrder) => {
                let item = fieldValue.find(item => item.displayOrder === oldOrder);
                if (isNoU(item)) {
                    let masterItem = masterFieldValue.find(item => item.displayOrder === oldOrder);
                    item = fieldValue.find(item => item.id === masterItem.id);

                    if (isNoU(item)) {
                        console.error(`no display order ${i} in collection`, fieldValue);
                        return {};
                    }
                }
                return item;
            });

            for (let i=0; i<newOrderList.length; i++) {
                newOrderList[i].displayOrder = i+1;
            }

            const reorderedFieldPath = fieldPath.slice(0, -1).concat(fieldToReorder);
            // dotProp.set(rootResource, reorderedFieldPath.join('.'), newOrderList);
            setBreakResource(masterResource, rootResource, reorderedFieldPath, newOrderList);
        } else {
            // Modify from root with full path
            // dotProp.set(rootResource, fieldPath.join('.'), value);
            setBreakResource(masterResource, rootResource, fieldPath, value);
        }

        state.pendingSaves[id + field] = {id, field, value};
        let alreadySaved = state.savedField[id + field];
        state.savedField[id + field] = isNoU(alreadySaved) ? false : alreadySaved;
        if (isNoU(errors) || errors.length === 0) {
          delete state.saveErrors[id + field];
        }
    })
    .forType(Types.RESOURCE_FIELD_SAVED)
    .reduce((state, {resourceType, id, field, saveData, modifiedBreak, rootResourceId, resourcePath}) => {
        // special handling for Prize
        if (resourceType === ResourceApi.Types.Prize) {
            dotProp.set(state.resources, id, saveData);
        }

        // special handling for dynamic content
        if (rootResourceId.type === ResourceApi.Types.Break && field.includes('dynamics')) {
            const rootResource = dotProp.get(state.resources, rootResourceId.id);
            dotProp.set(rootResource, resourcePath.join('.'), saveData);
            dotProp.set(state.resources, rootResourceId.id, rootResource);

            state.savedField[id + field] = true;
            delete state.pendingSaves[id + field];
        }

        // strip any array resource manipulation out
        field = trim(field.replace("#", "").replace(/X[0-9]*/, ""), ".");

        // using a white list of fields to set after a save
        // instead of setting entire object, which might override anything a user is inputting
        let triggerFieldSet = setAfterSaveConfig[resourceType];

        // Check if resource type is registered
        if (!isNoU(triggerFieldSet)) {
            let fieldsToSet = triggerFieldSet[field];

            // Check if field is registered to set additional fields
            if (!isNoU(fieldsToSet)) {
                let modifiedResource = state.resources[rootResourceId.id];
                for (let field of fieldsToSet) {
                    let value = dotProp.get(saveData, field);
                    let path = resourcePath.length > 0 ? resourcePath.join('.') + `.${field}` : field;
                    dotProp.set(modifiedResource, path, value);
                }
            }
        }

        updateModifiedBreak(state, modifiedBreak);

        state.savedField[id + field] = true;
        delete state.pendingSaves[id + field];
    })
    .forType(Types.SAVE_RESOURCE_CHANGELIST_SUBMIT)
      .reduce((state, {id, changelist}) => {
        for (const field of Object.keys(changelist)) {
            const value = changelist[field];
            state.pendingSaves[id + field] = {id, field, value};

            state.savedField[id + field] = !!state.savedField[id + field];
            delete state.saveErrors[id + field];
        }
    })
    .forType(Types.SAVE_RESOURCE_CHANGELIST_SUCCESS)
    .reduce((state, {id, changelist, rootResourceId, resourcePath}) => {
        let modifiedResource = state.resources[rootResourceId || id];
        if (resourcePath && resourcePath.length > 0) {
            modifiedResource = dotProp.get(modifiedResource, resourcePath.join('.'));
        }

        let keys = Array.isArray(changelist) ? changelist.map((change) => change.field) : Object.keys(changelist);

        let valuesByKeys = Array.isArray(changelist) ? changelist.reduce((prev, curr) => {
            prev[curr.field] = curr.value;
            return prev;
        }, {}) : changelist;

        for (let field of keys) {
            let value = valuesByKeys[field];
            dotProp.set(modifiedResource, field, value);

            state.savedField[id + field] = true;
            delete state.pendingSaves[id + field];
        }
    })
    .forType(Types.SAVE_RESOURCE_CHANGELIST_ERROR)
    .reduce((state, {id, changelist, error}) => {
        for (const field of Object.keys(changelist)) {
            state.saveErrors[id + field] = {
                errorCode: error.errorCode,
                failedValidations: error.failedValidations,
                sectionKeyPath: error.sectionKeyPath
            };

            delete state.pendingSaves[id + field];
        }
    })
    .forType(Types.SAVE_RESOURCE_FIELD_ERROR)
    .reduce((state, {id, field, value, error, rootResourceId, fieldPath}) => {
        let rootResource = state.resources[rootResourceId.id];
        dotProp.set(rootResource, fieldPath.join('.'), value);

        state.saveErrors[id + field] = {
            errorCode: error.errorCode,
            failedValidations: error.failedValidations,
            sectionKeyPath: error.sectionKeyPath
        };

        delete state.pendingSaves[id + field];
    })
    .forType(Types.UPDATE_PAGE_NUMBER_FOR_RESOURCE)
    .reduce((state, {resourceType, page}) => {
        let resourcePage = state.resourcePages[resourceType] || emptyPageFactory();
        resourcePage.page = page;
        return state;
    })
    .forType(Types.UPDATE_PAGE_COUNT_FOR_RESOURCE)
    .reduce((state, {resourceType, count}) => {
        let resourcePage = state.resourcePages[resourceType] || emptyPageFactory();
        resourcePage.count = count;
        return state;
    })
    .forType(Types.CLEAR_SAVE_ERRORS)
    .reduce((state) => {
        state.saveErrors = {};
    })
    .forType(SectionActionTypes.CLEAR_SECTION_SAVE_ERRORS)
    .reduce((state, {sectionKey}) => {
        const saveErrorKeys = Object.keys(state.saveErrors);
        const matchingKey = saveErrorKeys.find(key => includes(state.saveErrors[key].sectionKeyPath, sectionKey));
        if (matchingKey) {
            delete state.saveErrors[matchingKey];
        }
    })
    .forType(UserActionTypes.LOGOUT_SUCCESS)
    .reduce((state, action) => {
        state.resources = {};
        state.resourcePages = makeDefaultResourcePages();
        state.allResources = makeDefaultAllResources();
        state.pendingSaves = {};
        state.saveErrors = {};
        state.savedField = {};
    })
    .forType("ERROR")
    .reduce((state, action) => {
        console.error("error on", state, action, action.error);
        // 419 gets throw if there is a bad token
        // if ((action.error.status === 403) || (action.error.status === 419)) {
        // } else {
        //     console.error("error on", state, action, action.error);
        // }
    })
    .forType(UserActionTypes.LOGOUT_SUCCESS)
    .reduce((state, action) => {
        // Clear all resources state when user logs out
        state.resources = {};
        state.resourcePages = makeDefaultResourcePages();
        state.allResources = makeDefaultAllResources();
        state.pendingSaves = {};
        state.saveErrors = {};
        state.savedField = {};
    })
    .build();

export default reducer;



// WEBPACK FOOTER //
// ./public/resources/reducer.js